export const APPKIT_DROPDOWN_NO_RESULT_MSG = 'No data found';

export interface AppkitDropdownItem {
  value: any;
  label: string;
  selected?: boolean;
  [key: string]: any;
}

export function mapToAppkitDropdownItems(
  options: any[] | null | undefined,
  optionLabel: string = 'label',
  optionValue?: string
): AppkitDropdownItem[] {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .filter((item) => item !== null && item !== undefined)
    .map((item, index) => {
      const label = String(
        item?.[optionLabel] ??
        item?.label ??
        item?.text ??
        item?.name ??
        item?.TEXT ??
        item?.LABEL ??
        ''
      ).trim();

      const value = optionValue != null && optionValue !== ''
        ? item?.[optionValue]
        : (item?.value ?? item?.id ?? index);

      return {
        ...item,
        label,
        value
      };
    })
    .filter((item) => item.label !== '' && item.label !== 'undefined' && item.label !== 'null');
}

export function getAppkitDropdownSelection(event: any): AppkitDropdownItem | null {
  const selected = Array.isArray(event?.selected)
    ? event.selected[0]
    : (event?.selected ?? null);
  return selected || null;
}

export function findAppkitItemByLabel(items: AppkitDropdownItem[], label: string): AppkitDropdownItem | null {
  if (!label || !Array.isArray(items)) {
    return null;
  }
  return items.find((item) => String(item?.label ?? '') === String(label)) ?? null;
}

export function findAppkitItemByValue(items: AppkitDropdownItem[], value: any): AppkitDropdownItem | null {
  if (value === null || value === undefined || !Array.isArray(items)) {
    return null;
  }
  const exact = items.find((item) => item?.value === value);
  if (exact) {
    return exact;
  }

  // Backend code keys arrive as both numbers and strings across endpoints, so a
  // strict match alone leaves the dropdown showing the placeholder for a value
  // the component considers selected.
  if (typeof value === 'object') {
    return null;
  }
  return items.find((item) =>
    item?.value !== null &&
    item?.value !== undefined &&
    typeof item.value !== 'object' &&
    String(item.value) === String(value)
  ) ?? null;
}

export function syncAppkitDropdownSelection(
  items: AppkitDropdownItem[],
  currentValue: any,
  optionLabel?: string,
  optionValue?: string
): AppkitDropdownItem | null {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  if (currentValue && typeof currentValue === 'object') {
    const label = optionLabel ? currentValue?.[optionLabel] : (currentValue?.label ?? currentValue?.text);
    const value = optionValue ? currentValue?.[optionValue] : currentValue?.value;
    return findAppkitItemByValue(items, value)
      ?? findAppkitItemByLabel(items, String(label ?? ''))
      ?? null;
  }

  // Falling back to items[0] made every dropdown render the first option as if
  // the user had picked it while the bound model was still empty, so dependent
  // fields gated on that model never appeared.
  if (currentValue === null || currentValue === undefined || currentValue === '') {
    return null;
  }

  return findAppkitItemByValue(items, currentValue)
    ?? findAppkitItemByLabel(items, String(currentValue))
    ?? null;
}

export function setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
  const label = item?.label ?? item?.text ?? item?.value ?? '';
  return `${contextLabel} option ${label}`;
}

export interface AppkitDropdownBinding {
  source: string;
  view: string;
  item: string;
  model: string;
  labelKey?: string;
  valueKey?: string;
}

export function refreshAppkitDropdownBindings(component: any, bindings: AppkitDropdownBinding[]): void {
  bindings.forEach((binding) => {
    const labelKey = binding.labelKey || 'CODE_DESCRIPTION';
    const valueKey = binding.valueKey || 'CODE_KEY';
    component[binding.view] = mapToAppkitDropdownItems(component[binding.source], labelKey, valueKey);
    component[binding.item] = syncAppkitDropdownSelection(
      component[binding.view],
      component[binding.model],
      labelKey,
      valueKey
    );
  });
}

export function handleAppkitValueSelect(
  event: any,
  component: any,
  itemKey: string,
  modelKey: string,
  afterChange?: () => void
): void {
  const selected = getAppkitDropdownSelection(event);
  component[itemKey] = selected;
  component[modelKey] = selected?.value ?? '';
  afterChange?.call(component);
}

export function handleAppkitObjectSelect(
  event: any,
  component: any,
  itemKey: string,
  modelKey: string,
  afterChange?: () => void
): void {
  const selected = getAppkitDropdownSelection(event);
  component[itemKey] = selected;
  component[modelKey] = selected || '';
  afterChange?.call(component);
}
